/* eslint-disable @typescript-eslint/no-empty-function */
import { useEffect, useRef, useState, ChangeEvent, WheelEvent } from "react";
import PopupManager from "@/components/PopupManager";
import { setGlobalPopupElement } from "@/store/features/globalSlice";
import { useAppDispatch } from "@/store/hooks";
import { useDebounce } from "@/hooks/useDebounce";
import { getS3ImageUrl } from "@/utils/routes";
import { getGiftList } from "@/requests/offer";

const ManageGiftQty = ({
  initialGifts = [],
  initialMaxGifts = 1,
  onSubmit = () => {},
}: {
  initialGifts?: GiftItemWithQuantity[];
  initialMaxGifts?: number;
  onSubmit?: (gifts: GiftItemWithQuantity[], maxGifts: number) => void;
}) => {
  const dispatch = useAppDispatch();

  const [gifts, setGifts] = useState<GiftItemWithQuantity[]>(initialGifts);
  const [maxGifts, setMaxGifts] = useState(initialMaxGifts);

  const moveItem = (index: number, direction: "up" | "down") => {
    setGifts((prev) => {
      const arr = [...prev];
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= arr.length) return prev;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      return arr;
    });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(gifts, maxGifts);
      }}
    >
      <div className="p-5 w-[80vw] max-w-[600px] mx-auto bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="flex items-center gap-4">
          <Asynchronous
            label="Search Gifts"
            onChange={(item) => {
              setGifts((prev) => {
                if (prev.find((i) => i.id === item.id)) {
                  return prev;
                }
                return [
                  ...prev,
                  {
                    ...item,
                    quantity: 1,
                  },
                ];
              });
            }}
          />

          <input
            type="number"
            placeholder="Max Gifts"
            className="w-32 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            min={1}
            value={maxGifts || ''}
            onWheel={(e: WheelEvent<HTMLInputElement>) => (e.target as HTMLInputElement).blur()}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              const qty = +value;

              if (qty > 0) {
                setMaxGifts(qty);
              }
            }}
          />
        </div>

        <div className="mt-5 overflow-y-auto h-[80vh] max-h-[350px]">
          {gifts.map((gift, index) => (
            <div
              key={gift.id}
              className="flex items-center gap-3 p-2 border-b border-gray-200"
            >
              {/* Reorder buttons */}
              <div className="flex flex-col gap-0.5">
                <button
                  type="button"
                  disabled={index === 0}
                  className={`text-xs leading-none p-0.5 ${
                    index === 0
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => moveItem(index, "up")}
                >
                  ▲
                </button>
                <button
                  type="button"
                  disabled={index === gifts.length - 1}
                  className={`text-xs leading-none p-0.5 ${
                    index === gifts.length - 1
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => moveItem(index, "down")}
                >
                  ▼
                </button>
              </div>

              <div className="flex-shrink-0">
                <img
                  src={getS3ImageUrl(gift.media || "")}
                  alt={gift.name}
                  width={50}
                  height={50}
                  style={{ objectFit: "cover" }}
                  className="rounded"
                />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{gift.name}</p>
              </div>

              <div className="w-20 flex-shrink-0">
                <input
                  type="number"
                  placeholder="Qty"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min={1}
                  value={gift.quantity || ''}
                  onWheel={(e: WheelEvent<HTMLInputElement>) => (e.target as HTMLInputElement).blur()}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    const qty = +value;

                    if (qty > 0) {
                      setGifts((prev) => {
                        const index = prev.findIndex(
                          (item) => item.id === gift.id,
                        );

                        if (index > -1 && prev[index]) {
                          prev[index] = { ...prev[index], quantity: qty };
                        }

                        return [...prev];
                      });
                    }
                  }}
                />
              </div>

              <div className="w-24 flex-shrink-0">
                <input
                  type="number"
                  placeholder="Amount"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min={0}
                  defaultValue={gift.amount || gift.price || ''}
                  onWheel={(e: WheelEvent<HTMLInputElement>) => (e.target as HTMLInputElement).blur()}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    const amt = +value;

                    if (!isNaN(amt) && amt >= 0) {
                      setGifts((prev) => {
                        const index = prev.findIndex(
                          (item) => item.id === gift.id,
                        );

                        if (index > -1 && prev[index]) {
                          prev[index] = { ...prev[index], amount: amt };
                        }

                        return [...prev];
                      });
                    }
                  }}
                />
              </div>

              <div className="flex-shrink-0">
                <button
                  type="button"
                  className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                  onClick={() => {
                    setGifts((prev) => {
                      return prev.filter((item) => item.id !== gift.id);
                    });
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        <hr className="my-2 border-gray-200" />

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
          >
            Submit
          </button>
        </div>
      </div>
    </form>
  );
};

export function Asynchronous({
  onChange = () => {},
  label,
}: {
  onChange?: (item: GiftItem) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<readonly GiftItem[]>([]);
  const [loading, setLoading] = useState(false);

  const active = useRef(true);

  const [searchText, setSearchText] = useState<string>("");
  const debouncedSearchText = useDebounce(searchText, 500);

  useEffect(() => {
    active.current = true;

    if (open) {
      setLoading(true);

      getGiftList({
        offset: 0,
        limit: 20,
        status: "",
        searchFor: debouncedSearchText,
      })
        .then((data: any) => {
          if (!active.current) {
            return;
          }

          const newOptions = data?.Data || [];

          setOptions(newOptions);
        })
        .finally(() => {
          if (active.current) {
            setLoading(false);
          }
        });
    }

    return () => {
      active.current = false;
    };
  }, [open, debouncedSearchText]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  return (
    <div className="relative w-full">
      <input
        type="text"
        placeholder={label}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center p-4">
              <svg className="animate-spin h-5 w-5 text-gray-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No options</div>
          ) : (
            options.map((option) => (
              <div
                key={option.id}
                onMouseDown={() => {
                  onChange(option);
                  setSearchText('');
                  setOpen(false);
                }}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
              >
                {option.name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default ManageGiftQty;
